// Smart Sash Admin Panel - System Service
// System health, version, and utility operations

import { SystemHealth, SystemStatistics, SystemVersion, WhiteboardEmailRequest } from '@/models';
import axios from 'axios';
import { axiosClient } from '../http/axiosClient';

const BASE_PATH = '/system';

type RawSystemVersion =
  | SystemVersion
  | string
  | {
      success?: boolean;
      data?: RawSystemVersion;
      version?: string;
      apiVersion?: string;
      buildDate?: string;
      environment?: string;
    };

const getPublicVersionUrl = (): string => {
  const baseUrl = axiosClient.defaults.baseURL;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const resolvedBaseUrl = baseUrl ? new URL(baseUrl, origin) : new URL(origin);

  return new URL('/version', resolvedBaseUrl.origin).toString();
};

const normalizeSystemVersion = (payload: RawSystemVersion): SystemVersion => {
  if (typeof payload === 'string') {
    return {
      version: payload,
      apiVersion: payload,
      buildDate: '',
      environment: '',
    };
  }

  if ('data' in payload && payload.data) {
    return normalizeSystemVersion(payload.data);
  }

  const version = payload.version ?? payload.apiVersion ?? '';
  const apiVersion = payload.apiVersion ?? payload.version ?? '';

  return {
    version,
    apiVersion,
    buildDate: payload.buildDate ?? '',
    environment: payload.environment ?? '',
  };
};

/**
 * System Service
 */
export const systemService = {
  /**
   * Get system version information
   */
  getVersion: async (): Promise<SystemVersion> => {
    try {
      const response = await axios.get<RawSystemVersion>(getPublicVersionUrl(), {
        headers: {
          Accept: 'application/json',
        },
        timeout: axiosClient.defaults.timeout,
      });

      return normalizeSystemVersion(response.data);
    } catch {
      const response = await axiosClient.get<RawSystemVersion>(`${BASE_PATH}/version`);
      return normalizeSystemVersion(response.data);
    }
  },

  /**
   * Get system health status
   */
  getHealth: async (): Promise<SystemHealth> => {
    const response = await axiosClient.get<SystemHealth>(`${BASE_PATH}/health`);
    return response.data;
  },

  /**
   * Get system statistics
   */
  getStatistics: async (): Promise<SystemStatistics> => {
    const response = await axiosClient.get<SystemStatistics>(`${BASE_PATH}/statistics`);
    return response.data;
  },

  /**
   * Send test email (whiteboard feature)
   */
  sendTestEmail: async (request: WhiteboardEmailRequest): Promise<void> => {
    await axiosClient.post(`${BASE_PATH}/email/test`, request);
  },

  /**
   * Ping API to check connectivity
   */
  ping: async (): Promise<{ message: string; timestamp: string }> => {
    const response = await axiosClient.get<{ message: string; timestamp: string }>(
      `${BASE_PATH}/ping`
    );
    return response.data;
  },

  /**
   * Get API configuration (public info only)
   */
  getConfiguration: async (): Promise<Record<string, unknown>> => {
    const response = await axiosClient.get<Record<string, unknown>>(`${BASE_PATH}/configuration`);
    return response.data;
  },
};

export default systemService;
